// components/Post/ImageCarousel.jsx
// Instagram-style swipeable image carousel with indicator dots

import { useState, useRef } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";

export default function ImageCarousel({ images = [], alt = "post" }) {
  const [current, setCurrent] = useState(0);
  const touchStart = useRef(null);

  if (!images || images.length === 0) return null;

  // Single image — no carousel needed
  if (images.length === 1) {
    return (
      <img
        src={images[0]}
        alt={alt}
        className="w-full h-full object-contain rounded-xl"
        onError={(e) => { e.target.style.display = "none"; }}
      />
    );
  }

  const goTo = (idx) => {
    if (idx >= 0 && idx < images.length) setCurrent(idx);
  };

  // Touch handlers for swipe
  const onTouchStart = (e) => {
    touchStart.current = e.touches[0].clientX;
  };
  const onTouchEnd = (e) => {
    if (touchStart.current === null) return;
    const diff = touchStart.current - e.changedTouches[0].clientX;
    if (Math.abs(diff) > 50) {
      if (diff > 0) goTo(current + 1);   // swipe left → next
      else goTo(current - 1);             // swipe right → prev
    }
    touchStart.current = null;
  };

  return (
    <div
      className="relative w-full h-full overflow-hidden bg-black"
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      {/* Images slider */}
      <div
        className="flex h-full transition-transform duration-300 ease-out"
        style={{ transform: `translateX(-${current * 100}%)` }}
      >
        {images.map((src, i) => (
          <div key={i} className="w-full h-full flex-shrink-0 flex items-center justify-center">
            <img
              src={src}
              alt={`${alt} ${i + 1}`}
              className="w-full h-full object-contain rounded-xl"
              onError={(e) => { e.target.style.display = "none"; }}
            />
          </div>
        ))}
      </div>

      {/* Left arrow */}
      {current > 0 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(current - 1); }}
          className="absolute left-2 top-1/2 -translate-y-1/2 w-8 h-8
            bg-black/50 backdrop-blur-sm rounded-full flex items-center
            justify-center text-white/80 hover:bg-black/70 transition z-10"
        >
          <ChevronLeft size={18} />
        </button>
      )}

      {/* Right arrow */}
      {current < images.length - 1 && (
        <button
          onClick={(e) => { e.stopPropagation(); goTo(current + 1); }}
          className="absolute right-2 top-1/2 -translate-y-1/2 w-8 h-8
            bg-black/50 backdrop-blur-sm rounded-full flex items-center
            justify-center text-white/80 hover:bg-black/70 transition z-10"
        >
          <ChevronRight size={18} />
        </button>
      )}

      {/* Counter badge — top right */}
      <div className="absolute top-3 right-3 bg-black/60 backdrop-blur-sm
        rounded-full px-2.5 py-0.5 text-white/80 text-xs font-medium z-10">
        {current + 1} / {images.length}
      </div>

      {/* Dot indicators — bottom center */}
      {images.length <= 10 && (
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5 z-10">
          {images.map((_, i) => (
            <button
              key={i}
              onClick={(e) => { e.stopPropagation(); goTo(i); }}
              className={`rounded-full transition-all duration-200
                ${i === current
                  ? "w-2 h-2 bg-white"
                  : "w-1.5 h-1.5 bg-white/40"
                }`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
